import { useState } from 'react'
import { CurecallSelect, CurecallSelectItem, ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { useQueryClient } from '@tanstack/react-query'
import { useSession } from 'src/providers/auth.provider'
import TimelineLink from './timeline/timeline-link.component'
import MedEventTimeline from './timeline/med-event-timeline.component'
import WorkflowTimeline from './timeline/workflow-timeline.component'
import { useParams } from 'react-router-dom'
import { useNotification } from 'src/providers/notification.provider'
import { useTranslation } from 'react-i18next'
import { PatientService } from 'src/services/patient.service'
import { useTestAccountPool } from 'src/providers/test-account-pool.provider'
import TimelineSkeleton from './timeline/timeline-skeleton.component'
import { TPatient } from 'src/types/patient'
import { TTimeline, TTimelineItem, TTimelineWorkflowItem } from './timeline.type'
import { useCurrentPatientFile } from '../../_components/patient-file.provider'
import { getFollowupTimeline } from './timeline-scope.util'
import TimelineEmptyState from './timeline/timeline-empty-state.component'

enum ETimelineScope {
  CURRENT = 'current',
  ALL = 'all',
}

interface Props {
  patient: TPatient
  patientTimeline?: TTimeline
}

const Timeline = ({ patient, patientTimeline }: Props) => {
  const params = useParams()
  const projectId = Number(params.projectId)
  const patientId = Number(params.patientId)
  const { currentUser } = useSession()
  const { currentPatientFollowup } = useCurrentPatientFile()
  const [isExecutingWorkflow, setIsExecutingWorkflow] = useState(false)
  const [scope, setScope] = useState(ETimelineScope.CURRENT)
  const [showPastMedEvents, setShowPastMedEvents] = useState(false)
  const { testAccountsPool } = useTestAccountPool()
  const { notify } = useNotification()
  const { t } = useTranslation()

  const queryClient = useQueryClient()
  const isDemo = !!(patient.isTestAccount && currentUser?.isAdminOrSales)

  const scopeOptions: CurecallSelectItem<ETimelineScope>[] = [
    {
      value: ETimelineScope.CURRENT,
      label: currentPatientFollowup?.Followup?.name || '',
    },
    {
      value: ETimelineScope.ALL,
      label: t('components.patient.patient-file.body.followup-details.timeline-scope-all'),
    },
  ]

  const handleClickWorkflow = async (workflow: TTimelineWorkflowItem) => {
    if (isExecutingWorkflow) return
    setIsExecutingWorkflow(true)
    try {
      const patientIds = testAccountsPool.map((patient) => patient.id)
      await PatientService.postPatientStartWorkflow({
        projectId,
        patientId,
        testAccountsPool: patientIds,
        ...workflow.manualTriggerData,
      })

      queryClient.setQueryData<{ timelineElements: TTimeline }>(
        ['patient-timeline', projectId, patientId],
        (oldData) => {
          if (!oldData) return { timelineElements: [] }
          const { timelineElements } = oldData
          let setDone = false
          const newData = timelineElements.map((timelineElement: TTimelineItem) => {
            // Since the timeline is ordered, once we mark an element done
            // we can mark all following elements done
            const isWorkflow = timelineElement.type === 'Workflow'
            setDone =
              setDone ||
              (isWorkflow &&
                (timelineElement as TTimelineWorkflowItem).manualTriggerData === workflow.manualTriggerData)
            return setDone ? { ...timelineElement, done: true } : timelineElement
          })
          return { timelineElements: newData }
        },
      )
    } catch (e) {
      notify({
        title: t('components.patient.patient-file.body.followup-details.timeline-error-title'),
        message: e,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    } finally {
      setTimeout(() => {
        setIsExecutingWorkflow(false)
      }, 1000)
    }
  }

  const followupTimeline =
    scope === ETimelineScope.CURRENT && patientTimeline && currentPatientFollowup
      ? getFollowupTimeline({
          timeline: patientTimeline,
          patientFollowupId: currentPatientFollowup.id,
          showPastMedEvents,
        })
      : undefined
  const displayedTimeline = followupTimeline?.items ?? patientTimeline

  if (scope === ETimelineScope.ALL && patientTimeline?.length === 0) {
    return null
  }

  return (
    <div className="c-p-6 c-w-full c-rounded-xl c-border-grey-300 c-border-2 timeline c-mb-5 c-h-max">
      <div className="c-flex c-items-center c-justify-between c-mb-2">
        <h3 className="c-font-bold c-text-start c-text-base">
          {t('components.patient.patient-file.body.followup-details.patient-followup')}
        </h3>
        <CurecallSelect
          selectedItem={scopeOptions.find((option) => option.value === scope)}
          onChange={(option) => setScope(option.value)}
          items={scopeOptions}
          placeholder={scopeOptions[0].label}
        />
      </div>
      {displayedTimeline?.length === 0 ? (
        <TimelineEmptyState followupName={currentPatientFollowup?.Followup?.name || ''} />
      ) : (
        <div className="c-flex c-flex-col c-items-center c-max-w-[80%]">
          <div className="c-border-l-[13px] c-border-r-[13px] c-border-b-[18px] c-border-l-transparent c-border-r-transparent c-border-b-primary" />
          <TimelineLink />
          {displayedTimeline === undefined ? (
            <TimelineSkeleton />
          ) : (
            displayedTimeline.map((item: TTimelineItem, index: number) => {
              const previousItemKey =
                index > 0
                  ? `${displayedTimeline[index - 1]?.date}-${displayedTimeline[index - 1]?.label}`
                  : null
              const currentItemKey = `${item.date}-${item.label}-${index}`
              if (previousItemKey === currentItemKey && item.done) {
                return null
              }
              return item.type === 'MedEvent' ? (
                <MedEventTimeline
                  isLast={index === displayedTimeline.length - 1}
                  index={index}
                  key={currentItemKey}
                  medEvent={item}
                />
              ) : (
                <WorkflowTimeline
                  isDemo={isDemo}
                  handleClickWorkflow={
                    handleClickWorkflow as unknown as (workflow: TTimelineWorkflowItem) => boolean
                  }
                  index={index}
                  workflow={item}
                  isLast={index === displayedTimeline.length - 1}
                  key={currentItemKey}
                  isExecutingWorkflow={isExecutingWorkflow}
                />
              )
            })
          )}
          {followupTimeline?.hasHiddenPastMedEvents && (
            <button
              type="button"
              onClick={() => setShowPastMedEvents(true)}
              className="c-text-primary c-underline c-text-sm c-mt-6 c-cursor-pointer"
            >
              {t('components.patient.patient-file.body.followup-details.timeline-see-past-med-events')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default Timeline
